import YAML from 'js-yaml';
import {
  isYamlFormValues,
  JsonSchema,
  YAML_FORM_SKIP_KEYS,
  YamlFormValues,
} from './types';

export const removeLineComments = (yaml: string) => {
  const lines = yaml.split('\n');
  const uncommentedLines = lines.filter((line) => !line.trim().startsWith('#'));
  return uncommentedLines.join('\n');
};

const isEmptyValue = (value: unknown): boolean => {
  if (value === undefined || value === null) {
    return true;
  }
  if (value === '') {
    return true;
  }
  if (Array.isArray(value) && value.length === 0) {
    return true;
  }
  if (isYamlFormValues(value) && Object.keys(value).length === 0) {
    return true;
  }
  return false;
};

export const omitEmptyOptionals = (
  values: YamlFormValues,
  schema: JsonSchema,
): YamlFormValues => {
  const properties = Object.assign(
    Object.create(null),
    schema.properties ?? {},
  ) as Record<string, JsonSchema>;
  const required = new Set(schema.required ?? []);
  const result: YamlFormValues = Object.create(null);

  Object.entries(values).forEach(([key, value]) => {
    if (!Object.hasOwn(properties, key) || YAML_FORM_SKIP_KEYS.includes(key)) {
      result[key] = value;
      return;
    }
    const propertySchema = properties[key];
    if (propertySchema.properties && isYamlFormValues(value)) {
      const nested = omitEmptyOptionals(value, propertySchema);
      if (!isEmptyValue(nested) || required.has(key)) {
        result[key] = nested;
      }
      return;
    }
    if (!isEmptyValue(value) || required.has(key)) {
      result[key] = value;
    }
  });

  return result;
};

export const parseYamlFormValues = (
  yaml: string,
  testName: string,
): YamlFormValues | null => {
  if (!yaml.trim()) {
    return null;
  }
  try {
    const parsed: unknown = YAML.load(removeLineComments(yaml));
    if (!Array.isArray(parsed) || parsed.length === 0) {
      return null;
    }
    const first = parsed[0];
    if (!isYamlFormValues(first) || !(testName in first)) {
      return null;
    }
    const inner = first[testName];
    if (inner === undefined || inner === null) {
      return {};
    }
    if (!isYamlFormValues(inner)) {
      return null;
    }
    return inner;
  } catch {
    return null;
  }
};

export const dumpYamlFormValues = (
  values: YamlFormValues,
  testName: string,
  schema: JsonSchema,
): string => {
  const cleaned = omitEmptyOptionals(values, schema);
  return YAML.dump([{ [testName]: cleaned }], {
    lineWidth: -1,
    noRefs: true,
  });
};

export const getValueAtPath = (
  values: YamlFormValues,
  path: string[],
): unknown => {
  let current: unknown = values;
  for (const key of path) {
    if (!isYamlFormValues(current)) {
      return undefined;
    }
    current = current[key];
  }
  return current;
};

export const setValueAtPath = (
  values: YamlFormValues,
  path: string[],
  value: unknown,
): YamlFormValues => {
  if (path.length === 0) {
    return values;
  }
  const [head, ...rest] = path;
  if (rest.length === 0) {
    const next = { ...values };
    if (value === undefined) {
      delete next[head];
    } else {
      next[head] = value;
    }
    return next;
  }
  const child = isYamlFormValues(values[head]) ? values[head] : {};
  return {
    ...values,
    [head]: setValueAtPath(child, rest, value),
  };
};
